import withParams from "@src/components/widgets/HOC/withParams";
import React, { useCallback, useMemo } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetInnovationsAdrienCachotParams } from "../interface";
import LandingInnovationAdrienCachot from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovationAdrienCachot/LandingInnovationAdrienCachot";
import useProductList, {
  InnovationProductAdditionalData,
} from "@src/components/widgets/hooks/useProductList";
import { useTranslation } from "@src/hooks/useTranslation";
import InnovationProductAdrienCachot from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovationAdrienCachot/InnovationProductAdrienCachot/InnovationProductAdrienCachot";
import { IInnovationProductAdrienCachotProps } from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovationAdrienCachot/InnovationProductAdrienCachot/InnovationProductAdrienCachot";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import SushishopVideo from "@src/lib/render-video/SushishopVideo/SushishopVideo";

const ProductItemRender = (
  props: IInnovationProductAdrienCachotProps<InnovationProductAdditionalData>
) => {
  const { product: flatProduct, onClick, ...rest } = props;
  const { id_product, actionType, link } = flatProduct;
  const { t } = useTranslation(["landing"]);
  const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");

  return (
    <InnovationProductAdrienCachot
      {...rest}
      ctaLabel={getSubmitLabel(false, t("Je commande", "landing"))}
      product={flatProduct}
      onClick={generateClick(
        actionType && actionType === "open_link"
          ? { type: "open_link", link: link || "#" }
          : { type: actionType || "product_card" }
      )}
    />
  );
};

const InnovationsAdrienCachot: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetInnovationsAdrienCachotParams>((props) => {
    const { p, params } = props;
    const { t } = useTranslation(["UI"]);

    const { prepareAdrienCachotProductList } = useProductList(params);

    const renderVideo = useCallback(
      (
        videoSlug: string,
        videoRef: React.MutableRefObject<HTMLVideoElement | null>,
        videoProps: React.VideoHTMLAttributes<HTMLVideoElement>
      ) => {
        const { poster, ...rest } = videoProps;
        return (
          <SushishopVideo
            wrapperStyles={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              aspectRatio: 437 / 523,
              mobile: {
                aspectRatio: "unset",
              },
            }}
            poster={true}
            customPosterImage={poster}
            ref={(ref) => {
              const innerVideoRef = ref?.getVideoRef();
              if (innerVideoRef) {
                videoRef.current = innerVideoRef;
              }
            }}
            slug={videoSlug}
            resize="full"
            /** Mobile : flux vidéo + poster PNG API plus légers (moins de decode au premier scroll). */
            // breakpoints={[
            //   {
            //     maxWidth: 768,
            //     videoParams: {
            //       quality: "480p",
            //       resize: "standard",
            //     },
            //   },
            // ]}
            // LCP-FIX: lazyLoad=true → <source> montés seulement au scroll (200px rootMargin),
            // évite 24 fetchs MP4 concurrents en début de navigation sur listing pages.
            lazyLoad
            preload="none"
            controls={false}
            autoPlay={false}
            loop={true}
            muted={true}
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        );
      },
      []
    );

    const renderProduct = useCallback(
      (data: IInnovationProductAdrienCachotProps, index: number) => {
        return (
          <ProductItemRender
            key={data.product.id_product + "-" + index}
            {...data}
            renderVideo={renderVideo}
          />
        );
      },
      [renderVideo]
    );

    const productList = useMemo(
      () => prepareAdrienCachotProductList(6),
      [prepareAdrienCachotProductList]
    );

    return (
      <LandingInnovationAdrienCachot<InnovationProductAdditionalData>
        products={productList}
        showLessLabel={t("Voir moins", "UI")}
        showMoreLabel={t("Voir plus", "UI")}
        renderProduct={renderProduct}
        title={p("title")}
      />
    );
  });

export default InnovationsAdrienCachot;
